<template>
  <div class="lookup-page">
    <div class="card">
      <div class="card-header">
        <h3>Consulta CPF</h3>
      </div>

      <div class="card-body">
        <div class="row">
          <div class="col-md-5">
            <label>CPF</label>
            <input
              v-model="cpf"
              v-mask="'###.###.###-##'"
              type="text"
              class="form-control"
              placeholder="000.000.000-00"
              @keyup.enter="searchCpf"
            >
          </div>

          <div class="col-md-3">
            <label>Consultar em</label>
            <select
              v-model="system"
              class="form-control"
            >
              <option value="">
                Selecione
              </option>
              <option value="th">
                TIH
              </option>
              <option value="chronos">
                Chronos Samu
              </option>
            </select>
          </div>

          <div class="col-md-3 d-flex align-items-end">
            <button
              class="btn btn-primary w-100"
              :disabled="loading"
              @click="searchCpf"
            >
              {{ loading ? "Consultando..." : "Consultar" }}
            </button>
          </div>
        </div>

        <div
          v-if="message"
          class="alert mt-3"
          :class="messageClass"
        >
          {{ message }}
        </div>

        <div
          v-if="form.id"
          class="mt-4"
        >
          <h4>Dados encontrados</h4>

          <div class="row">
            <div class="col-md-6">
              <label>Nome</label>
              <input
                v-model="form.name"
                type="text"
                class="form-control"
                readonly
              >
            </div>

            <div class="col-md-6">
              <label>CPF</label>
              <input
                v-model="form.cpf"
                type="text"
                class="form-control"
                readonly
              >
            </div>
          </div>

          <div class="row mt-3">
            <div class="col-md-6">
              <label>Email</label>
              <input
                v-model="form.email"
                type="text"
                class="form-control"
                readonly
              >
            </div>

            <div class="col-md-6">
              <label>Status</label>
              <input
                :value="statusLabel"
                type="text"
                class="form-control"
                readonly
              >
            </div>
          </div>

          <div class="row mt-3">
            <div class="col-md-6">
              <label>Tipo profissional</label>
              <input
                :value="professionalTypeLabel"
                type="text"
                class="form-control"
                readonly
              >
            </div>

            <div class="col-md-6">
              <label>Celular</label>
              <input
                v-model="form.phone"
                type="text"
                class="form-control"
                readonly
              >
            </div>
          </div>

          <div class="mt-4">
            <button
              class="btn btn-secondary"
              @click="clear"
            >
              Limpar
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { mask } from 'vue-the-mask'
import { isValidCpf } from '@/utils/validators'
import { apiErrorMessage } from '@/utils/apiError'

export default {
  name: 'LookupCrud',

  directives: { mask },

  data() {
    return {
      cpf: '',
      system: '',
      loading: false,
      message: '',
      messageType: '',

      form: {
        id: null,
        name: '',
        cpf: '',
        email: '',
        status: null,
        phone: '',
        professionalTypeId: null,
      },
    }
  },

  computed: {
    messageClass() {
      if (this.messageType === 'success') {
        return 'alert-success'
      }

      if (this.messageType === 'error') {
        return 'alert-danger'
      }

      return 'alert-info'
    },

    statusLabel() {
      if (this.form.status === 1 || this.form.status === '1') {
        return 'Ativo'
      }

      if (this.form.status === 0 || this.form.status === '0') {
        return 'Inativo'
      }

      return ''
    },

    professionalTypeLabel() {
      if (
        this.form.professionalTypeId === 2
        || this.form.professionalTypeId === '2'
      ) {
        return 'Técnico de enfermagem'
      }

      if (
        this.form.professionalTypeId === 3
        || this.form.professionalTypeId === '3'
      ) {
        return 'Enfermeiro'
      }

      return ''
    },
  },

  methods: {
    clearForm() {
      this.form = {
        id: null,
        name: '',
        cpf: '',
        email: '',
        status: null,
        phone: '',
        professionalTypeId: null,
      }
    },

    normalizeCpf(cpf) {
      return String(cpf || '').replace(/\D/g, '')
    },

    async searchCpf() {
      const enteredCpf = this.normalizeCpf(this.cpf)

      this.message = ''
      this.messageType = ''
      this.clearForm()

      if (!enteredCpf) {
        this.message = 'Digite um CPF para consultar.'
        this.messageType = 'error'
        return
      }

      if (!isValidCpf(enteredCpf)) {
        this.message = 'CPF inválido. Confira os números digitados.'
        this.messageType = 'error'
        return
      }

      if (!this.system) {
        this.message = 'Selecione onde deseja consultar o CPF.'
        this.messageType = 'error'
        return
      }

      this.loading = true

      try {
        const api = `${this.$store.state.api}lookup/cpf`

        const response = await axios.get(api, {
          params: {
            cpf: enteredCpf,
            sistema: this.system,
          },
          timeout: 40000,
        })

        const result = response.data
        const data = result.data || {}

        const record = this.system === 'th'
          ? data.tripulante || data
          : data.chronos || data

        if (!record || Object.keys(record).length === 0) {
          this.message = result.message || 'CPF não encontrado.'
          this.messageType = 'error'
          return
        }

        this.form = {
          id: record.id || record.tripulante_id || record.user_id || 1,

          name:
            record.tripulante_nome
            || record.name
            || record.nome
            || '',

          cpf:
            record.tripulante_cpf
            || record.cpf
            || '',

          email:
            record.tripulante_email
            || record.email
            || '',

          status:
            record.tripulante_status
            ?? record.status
            ?? null,

          phone:
            record.tripulante_celular
            || record.phone
            || record.celular
            || '',

          professionalTypeId:
            record.tipo_profissional_id
            ?? record.professional_type_id
            ?? null,
        }

        this.message = result.message
          || `CPF encontrado no ${
            this.system === 'th' ? 'TIH' : 'Chronos Samu'
          }.`

        this.messageType = 'success'
      } catch (error) {
        if (error.code === 'ECONNABORTED') {
          this.message = 'A consulta demorou mais que o esperado. Tente novamente.'
          this.messageType = 'error'
        } else if (error.response && error.response.status === 404) {
          this.message = error.response.data.message || 'CPF não encontrado.'
          this.messageType = 'error'
        } else {
          this.message = apiErrorMessage(error, 'Erro ao consultar o CPF.')
          this.messageType = 'error'
        }

        console.error('Erro ao consultar CPF:', error)
      } finally {
        this.loading = false
      }
    },

    clear() {
      this.cpf = ''
      this.system = ''
      this.message = ''
      this.messageType = ''
      this.clearForm()
    },
  },
}
</script>

<style scoped>
.lookup-page {
  padding: 20px;
}

.card {
  border-radius: 8px;
}

.card-header h3 {
  margin: 0;
}

.mt-3 {
  margin-top: 1rem;
}

.mt-4 {
  margin-top: 1.5rem;
}

.w-100 {
  width: 100%;
}
</style>
